import {
    ChangeDetectionStrategy,
    Component,
    Injectable,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { licensedFilterList } from '@components/shared/license/license.constants';
import {
    enumMapper,
    ILicenseBackOffice,
    ODataResponse,
    RobotType,
} from '@models';
import {
    encodeFilters,
    GRID_SERVICE,
    IGridSource,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import {
    LicenseUrlMapBackOffice,
    MachineService,
    ModalService,
    ODataQueryOptions,
    RuntimeLicenseService,
} from '@services';
import { IGridDataEntry } from '@uipath/angular/components/ui-grid';
import UiValidators, { INT32_MAX } from '@validators';

import {
    map,
    takeUntil,
} from 'rxjs/operators';

import { RuntimeEditModalComponent } from '../../modals';

type ITrackedLicenseBackOffice = IGridDataEntry & ILicenseBackOffice;

@Injectable()
export class BackOfficeSource implements IGridSource<ODataQueryOptions, ODataResponse<ITrackedLicenseBackOffice>> {
    public licenseType!: LicenseUrlMapBackOffice;

    public get mapper() {
        return this._runtimeLicense.mapper;
    }

    constructor(
        private _runtimeLicense: RuntimeLicenseService,
    ) { }

    public get(qo: ODataQueryOptions) {
        return this._runtimeLicense.getLicenseDetails(this.licenseType, qo)
            .pipe(
                map(res => ({
                    data: res.data.map(license => ({
                        ...license,
                        id: license.key,
                    })),
                    total: res.total,
                })),
            );
    }
}

// FIXME: expose these from a common source
const ROBOT_TYPES = enumMapper(RobotType, {
    resourceKey: 'RobotType',
});

@Component({
    selector: 'ui-runtime-license',
    templateUrl: './runtime-license.grid.html',
    styleUrls: ['./../license.details.scss'],
    providers: [
        MachineService,
        RuntimeLicenseService,
        {
            provide: GRID_SERVICE,
            useClass: BackOfficeSource,
        },
        {
            provide: STATE_HANDLER,
            useValue: urlStateHandler,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class RuntimeLicenseComponent
    extends ODataGridComponent<ITrackedLicenseBackOffice, number>
    implements OnInit {

    @Input()
    public licenseType!: LicenseUrlMapBackOffice;

    public licensedFilterList = licensedFilterList();
    public validators = UiValidators.range(1, INT32_MAX);
    public encodeFilters = encodeFilters;

    public isMachinesEditGranted$ = this._appState.user.permissions.isGranted('Machines.Edit');

    private _modalService = this._injector.get<ModalService>(ModalService);
    private _licenseService = this._injector.get<RuntimeLicenseService>(RuntimeLicenseService);

    ngOnInit() {
        const svc = this._service as unknown as BackOfficeSource;
        svc.licenseType = this.licenseType;
    }

    public getRobotType = (type: string) => ROBOT_TYPES.find(t => t.label === type);

    public editRuntime(entry: ILicenseBackOffice) {
        this._modalService
            .openModal(RuntimeEditModalComponent, {
                id: entry.machineId,
                name: entry.machineName,
                nonProductionSlots: entry.runtimes,
                unattendedSlots: entry.runtimes,
            }, {
                    data: this.licenseType,
                })
            .afterClosedResult()
            .subscribe(() => this._refresh$.next());
    }

    public handleMachineToggle(
        entry: ITrackedLicenseBackOffice,
        elem: MatSlideToggle,
    ) {
        if (entry.enabled) {
            elem.checked = true;
            this._modalService.onConfirm(
                null,
                () => {
                    elem.checked = false;
                    this._setMachineState(entry, false);
                },
                () => { },
                this._translate.instant('license_runtime_grid_disable_confirmation', {
                    machineName: entry.machineName,
                }),
            );
        } else {
            elem.checked = true;
            this._setMachineState(entry, true);
        }
    }

    private _setMachineState(entry: ITrackedLicenseBackOffice, active: boolean) {
        this.loading$.next(true);

        this._grid.dataManager.patchRow(entry.id, {
            enabled: active,
        });

        this._licenseService
            .toggleEnabled(entry, this.licenseType)
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(
                () => this._refresh$.next(),
                () => this._refresh$.next(),
            );
    }
}
